/**
 * Label / tone lookups for the balance-alert operator surface (#1892).
 *
 * Pure functions in their own module so the switch's three-way reading can be
 * asserted directly. It is a three-way and not a boolean on purpose — see
 * {@link readSwitchState}.
 */
import type {
  AccountBalanceAlertSettings,
  AccountBalanceAlertSeverity,
  AccountBalanceVerdict,
} from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import type { ChipTone } from '../../ui/index.ts';

/**
 * What the switch is saying, as three states rather than two.
 *
 * `enabled === false` is the ORDINARY state of a freshly promoted
 * environment: migration `0031` seeds the row closed precisely so a promote
 * cannot produce a first mail until somebody opens it. So "off" alone is not
 * a message — the useful question is whether anybody has ever decided
 * anything, and `updated_at` is the only field that answers it.
 *
 *   - `never_opened`  — seeded default still standing; nobody has opened this
 *                       yet, and NO customer has ever been mailed from here.
 *   - `closed`        — an operator turned it off. Something happened; the
 *                       reason field and the actor are the story.
 *   - `open`          — the next pass mails.
 *
 * Collapsing the first two into one "disabled" state is the mistake this type
 * exists to prevent: they read identically on a lamp and mean opposite things
 * to whoever finds the page.
 */
export type BalanceAlertSwitchState = 'open' | 'closed' | 'never_opened';

export function readSwitchState(
  settings: AccountBalanceAlertSettings,
): BalanceAlertSwitchState {
  if (settings.enabled) return 'open';
  return settings.updated_at === null ? 'never_opened' : 'closed';
}

const SWITCH_STATE_TONE: Record<BalanceAlertSwitchState, ChipTone> = {
  open: 'ok',
  // Both closed states are loud. `warn` rather than `neutral` because a grey
  // pill is what "everybody assumed mail was going out" looks like.
  closed: 'bad',
  never_opened: 'warn',
};

const SWITCH_STATE_LABEL: Record<BalanceAlertSwitchState, I18nKey> = {
  open: 'balance_alerts.switch.state_open',
  closed: 'balance_alerts.switch.state_closed',
  never_opened: 'balance_alerts.switch.state_never_opened',
};

export function switchStateTone(state: BalanceAlertSwitchState): ChipTone {
  return SWITCH_STATE_TONE[state];
}

export function switchStateLabel(state: BalanceAlertSwitchState): I18nKey {
  return SWITCH_STATE_LABEL[state];
}

const SEVERITY_TONE: Record<AccountBalanceAlertSeverity, ChipTone> = {
  warning: 'warn',
  critical: 'bad',
};

const SEVERITY_LABEL: Record<AccountBalanceAlertSeverity, I18nKey> = {
  warning: 'balance_alerts.severity.warning',
  critical: 'balance_alerts.severity.critical',
};

export function severityTone(severity: AccountBalanceAlertSeverity): ChipTone {
  return SEVERITY_TONE[severity];
}

export function severityLabel(severity: AccountBalanceAlertSeverity): I18nKey {
  return SEVERITY_LABEL[severity];
}

const VERDICT_LABEL: Record<AccountBalanceVerdict, I18nKey> = {
  healthy: 'balance_alerts.verdict.healthy',
  warning: 'balance_alerts.severity.warning',
  critical: 'balance_alerts.severity.critical',
  indeterminate: 'balance_alerts.verdict.indeterminate',
};

const VERDICT_TONE: Record<AccountBalanceVerdict, ChipTone> = {
  healthy: 'ok',
  warning: 'warn',
  critical: 'bad',
  indeterminate: 'neutral',
};

export function verdictLabel(verdict: AccountBalanceVerdict): I18nKey {
  return VERDICT_LABEL[verdict];
}

export function verdictTone(verdict: AccountBalanceVerdict): ChipTone {
  return VERDICT_TONE[verdict];
}

/**
 * Whether the next pass would actually put a message in this account's inbox.
 *
 * Three things have to be true at once and the table shows all three, but the
 * verdict on its own is the one an operator reads as "they get mailed" — so it
 * is computed here rather than left implied by a row of chips:
 *
 *   - the channel is open (`alerts_enabled`);
 *   - this account is not muted;
 *   - the dedup table does not already hold a severity at or above the
 *     verdict, which would make the pass treat it as already-announced.
 */
export function wouldMail(entry: {
  readonly alertsEnabled: boolean;
  readonly suppressed: boolean;
  readonly verdict: AccountBalanceVerdict;
  readonly activeAlertSeverity: AccountBalanceAlertSeverity | null;
}): boolean {
  if (!entry.alertsEnabled) return false;
  if (entry.suppressed) return false;
  if (entry.activeAlertSeverity === null) return true;
  // The stored peak ratchets: an armed `critical` swallows a later `warning`,
  // but an armed `warning` still escalates into a `critical`.
  return entry.activeAlertSeverity === 'warning' && entry.verdict === 'critical';
}
